import { Controller } from "@hotwired/stimulus";
import { Turbo } from "@hotwired/turbo-rails";

let pendingNavigation = null;

export default class extends Controller {
  static targets = [
    "panel",
    "drawer",
    "open",
    "close",
    "count",
    "results",
    "error",
  ];
  static values = { loading: String, error: String, unavailable: String };

  connect() {
    this.originalCount = this.countTarget.textContent;
    this.originalDescription =
      this.countTarget.getAttribute("aria-describedby");
    this.placeholder = document.createComment("catalog filters");
    this.panelTarget.before(this.placeholder);
    this.media = window.matchMedia("(max-width: 760px)");
    this.resize = () => this.syncLayout();
    this.media.addEventListener("change", this.resize);
    this.syncLayout();
    if (pendingNavigation?.pathname === window.location.pathname) {
      this.countTarget.focus({ preventScroll: true });
    }
    pendingNavigation = null;
  }

  disconnect() {
    this.media.removeEventListener("change", this.resize);
    this.closeDrawer(false);
  }

  syncLayout() {
    const wasOpen = this.drawerTarget.open;
    this.closeDrawer(false);
    this.openTarget.hidden = !this.media.matches;
    this.closeTarget.hidden = !this.media.matches;
    if (this.media.matches) {
      this.drawerTarget.append(this.panelTarget);
    } else {
      this.placeholder.after(this.panelTarget);
      if (wasOpen) this.panelTarget.querySelector("input").focus();
    }
  }

  open() {
    this.drawerTarget.showModal();
    document.body.classList.add("catalog-drawer-open");
    this.openTarget.setAttribute("aria-expanded", "true");
    this.panelTarget.querySelector("input[type=search]").focus();
  }

  close(event) {
    event?.preventDefault();
    this.closeDrawer(true);
  }

  keyboard(event) {
    if (event.key === "Escape") {
      this.close(event);
      return;
    }
    if (event.key !== "Tab") return;
    const controls = [
      ...this.panelTarget.querySelectorAll("button, input, select, a[href]"),
    ].filter((element) => !element.disabled && element.getClientRects().length);
    const first = controls[0];
    const last = controls.at(-1);
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  closeDrawer(restoreFocus) {
    const wasOpen = this.drawerTarget.open;
    if (wasOpen) this.drawerTarget.close();
    document.body.classList.remove("catalog-drawer-open");
    this.openTarget.setAttribute("aria-expanded", "false");
    if (wasOpen && restoreFocus && !this.openTarget.hidden)
      this.openTarget.focus();
  }

  closed() {
    document.body.classList.remove("catalog-drawer-open");
    this.openTarget.setAttribute("aria-expanded", "false");
  }

  backdrop(event) {
    if (event.target !== this.drawerTarget) return;
    const bounds = this.drawerTarget.getBoundingClientRect();
    if (
      event.clientX < bounds.left ||
      event.clientX > bounds.right ||
      event.clientY < bounds.top ||
      event.clientY > bounds.bottom
    )
      this.close(event);
  }

  submit(event) {
    const url = new URL(event.target.action);
    url.search = new URLSearchParams(new FormData(event.target)).toString();
    this.startLoading(url);
  }

  search(event) {
    if (event.target.matches("form.site-search")) this.submit(event);
  }

  category(event) {
    if (!event.target.matches('select[name="filter[category]"]')) return;
    document.querySelectorAll('select[name="filter[category]"]').forEach((select) => {
      if (select === event.target) return;
      if (![...select.options].some((option) => option.value === event.target.value))
        select.add(event.target.selectedOptions[0].cloneNode(true));
      select.value = event.target.value;
    });
  }

  sort(event) {
    const url = new URL(event.target.value, window.location.href);
    this.startLoading(url);
    Turbo.visit(url.href);
  }

  navigate(event) {
    const link = event.target.closest("a[href]");
    if (
      !link ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    )
      return;
    this.startLoading(new URL(link.href));
  }

  startLoading(url) {
    this.destination = url;
    pendingNavigation = url;
    this.loading = true;
    this.closeDrawer(false);
    this.errorTarget.hidden = true;
    this.countTarget.removeAttribute("aria-describedby");
    this.countTarget.textContent = this.loadingValue;
    this.resultsTarget.setAttribute("aria-busy", "true");
    this.resultsTarget
      .querySelectorAll(".product-grid, .catalog-pagination, .empty-state")
      .forEach((element) => {
        element.hidden = true;
      });
  }

  checkResponse(event) {
    if (this.loading && !event.detail.fetchResponse.succeeded) {
      event.preventDefault();
      this.requestFailed();
    }
  }

  requestFailed(event) {
    if (!this.loading) return;
    event?.preventDefault();
    this.loading = false;
    pendingNavigation = null;
    this.resultsTarget.setAttribute("aria-busy", "false");
    this.countTarget.textContent = this.unavailableValue;
    this.errorTarget.querySelector("[role=alert]").textContent =
      this.errorValue;
    this.errorTarget.hidden = false;
  }

  retry() {
    this.startLoading(this.destination);
    Turbo.visit(this.destination.href);
  }

  prepareForCache() {
    this.closeDrawer(false);
    this.placeholder.after(this.panelTarget);
    this.countTarget.textContent = this.originalCount;
    if (this.originalDescription)
      this.countTarget.setAttribute(
        "aria-describedby",
        this.originalDescription,
      );
    this.resultsTarget.setAttribute("aria-busy", "false");
    this.errorTarget.hidden = true;
    this.resultsTarget
      .querySelectorAll(".product-grid, .catalog-pagination, .empty-state")
      .forEach((element) => {
        element.hidden = false;
      });
    this.placeholder.remove();
  }
}
